import type { ReactNode } from 'react';
import { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { isPathAllowed } from '@/utils/domainConfig';
import { checkGeoRestrictions as checkGeo } from '@/utils/geoCheck';
import { isSanctioned, isTokenInteractionBlocked } from '@/config/geo';

export function DomainRouter({ children }: { children: ReactNode }) {
  const navigate = useNavigate();
  const location = useLocation();
  
  useEffect(() => {
    // A path not allowed on this domain sends the visitor home. It must not land on
    // a content page — a blocked URL like /whitepaper or /standards-mark is held back
    // deliberately, and redirecting into /token would surface the token to someone
    // who never asked for it.
    if (!isPathAllowed(location.pathname)) {
      navigate('/');
    }
  }, [location.pathname, navigate]);
  
  // Check geo-restrictions
  useEffect(() => {
    const performGeoCheck = async () => {
      try {
        const { countryCode, stateCode, isTest } = await checkGeo();

        if (isTest && import.meta.env.DEV) {
          console.log(`[GEO-TEST] Testing with country: ${countryCode}${stateCode ? `, state: ${stateCode}` : ''}`);
        }

        if (countryCode) {
          if (isSanctioned(countryCode, stateCode)) {
            window.location.href = '/blocked.html';
            return;
          }

          const isTokenPath = location.pathname === '/token'
            || location.pathname.startsWith('/token/')
            || location.pathname === '/tokenomics'
            || location.pathname.startsWith('/docs/Tokenomics/');

          if (isTokenPath && isTokenInteractionBlocked(countryCode, stateCode)) {
            navigate('/token/restricted-countries');
            return;
          }
        }
      } catch (error) {
        console.error('Geo-check failed:', error);
        // In case of error, allow access (fail open)
      }
    };

    performGeoCheck();
  }, [location.pathname, navigate]);
  
  return <>{children}</>;
}
